import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Loader2, Zap, TrendingUp, Trophy, CheckCircle2, Lock, Gauge, Flame,
  ArrowUpRight, ArrowDownRight, HelpCircle, X, Sparkles, ChevronRight,
} from "lucide-react";
import api from "../lib/apiClient";
import { XpBar } from "../components/XpBar";
import { fmtDate, CATEGORY_META } from "../lib/gameData";

const RANGES = [{ k: "7d", l: "7 j" }, { k: "30d", l: "30 j" }, { k: "90d", l: "90 j" }, { k: "all", l: "Tout" }];

export default function Progression() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reco, setReco] = useState(null);
  const [history, setHistory] = useState([]);
  const [range, setRange] = useState("30d");
  const [showExplain, setShowExplain] = useState(false);
  const [showAllXp, setShowAllXp] = useState(false);

  useEffect(() => {
    api.get("/progression").then((r) => setData(r.data));
    api.get("/progression/recommendations").then((r) => setReco(r.data));
  }, []);

  useEffect(() => {
    api.get(`/score-history?range=${range}`).then((r) => setHistory(r.data.history));
  }, [range]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  const lvl = data.level_info;
  const sw = data.strengths_weaknesses;
  const txns = showAllXp ? data.transactions : data.transactions.slice(0, 5);
  const chartData = history.map((h) => ({ day: h.day.slice(5), score: h.score }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Ma progression</h1>
        <p className="text-zinc-500 text-sm mt-1">Où tu en es, ce que tu as accompli, comment progresser.</p>
      </div>

      {/* Level + Score de progression */}
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 bg-[#18181E] border border-zinc-800 rounded-3xl p-6 relative grain" data-testid="progression-level">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Niveau {lvl.level} — {data.career_title}</span>
                <p className="font-display text-3xl font-extrabold mt-1"><span className="gold-text">{data.total_xp}</span> / {lvl.is_max ? data.total_xp : lvl.next_threshold} XP</p>
              </div>
              <div className="w-16 h-16 rounded-2xl gold-gradient flex items-center justify-center glow-gold font-display font-extrabold text-3xl text-black">{lvl.level}</div>
            </div>
            <div className="mt-5">
              <XpBar value={data.total_xp - lvl.current_threshold} max={lvl.is_max ? 1 : lvl.xp_for_next_level} />
              <p className="text-sm text-zinc-500 mt-2">{lvl.is_max ? "Niveau maximum atteint" : `${lvl.xp_remaining} XP avant le niveau ${lvl.next_level}`}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#18181E] border border-amber-500/30 rounded-3xl p-6 flex flex-col items-center justify-center text-center relative" data-testid="artist-score-block">
          <div className="flex items-center gap-2 text-amber-400 mb-2"><Gauge className="w-5 h-5" /><span className="text-xs font-mono uppercase tracking-wider">Score de progression</span></div>
          <ScoreRing value={data.artist_score} />
          <div className="flex items-center gap-2 mt-3">
            <span className="px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-xs font-mono text-amber-400 flex items-center gap-1"><Flame className="w-3.5 h-3.5" /> {data.streak} j</span>
            <button onClick={() => setShowExplain(true)} data-testid="why-score-btn" className="px-2.5 py-1 rounded-full border border-zinc-700 text-xs text-zinc-300 hover:border-amber-500/50 flex items-center gap-1">
              <HelpCircle className="w-3.5 h-3.5" /> Pourquoi ?
            </button>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div>
        <h2 className="font-display text-xl font-bold mb-4">Les 6 catégories</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="categories-grid">
          {data.categories.map((c) => {
            const meta = CATEGORY_META[c.key];
            const Icon = meta.icon;
            return (
              <div key={c.key} className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 card-hover" data-testid={`category-${c.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${meta.color}22` }}>
                    <Icon className="w-5 h-5" style={{ color: meta.color }} />
                  </div>
                  <span className="font-display text-lg font-extrabold">{c.score}<span className="text-xs text-zinc-600">/100</span></span>
                </div>
                <p className="text-sm font-medium">{meta.label}</p>
                <div className="mt-2 h-2 rounded-full bg-zinc-800 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${c.score}%`, backgroundColor: meta.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Strengths / weaknesses */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-2xl p-5" data-testid="strength-card">
          <div className="flex items-center gap-2 text-emerald-400 mb-1"><ArrowUpRight className="w-4 h-4" /><span className="text-xs font-mono uppercase tracking-wider">Ton point fort</span></div>
          <p className="font-display text-lg font-bold">{sw.strength.label} — {sw.strength.score}/100</p>
          <p className="text-sm text-zinc-400 mt-1">{sw.strength.message}</p>
        </div>
        <div className="bg-red-500/5 border border-red-500/30 rounded-2xl p-5" data-testid="weakness-card">
          <div className="flex items-center gap-2 text-red-400 mb-1"><ArrowDownRight className="w-4 h-4" /><span className="text-xs font-mono uppercase tracking-wider">Ton axe de progression</span></div>
          <p className="font-display text-lg font-bold">{sw.weakness.label} — {sw.weakness.score}/100</p>
          <p className="text-sm text-zinc-400 mt-1">{sw.weakness.message}</p>
        </div>
      </div>

      {/* How to progress (IA recommendations) */}
      {reco && (
        <div className="bg-[#18181E] border border-amber-500/30 rounded-3xl p-6 relative overflow-hidden" data-testid="how-to-progress">
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-amber-500/10 blur-3xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 text-amber-400 mb-2"><Sparkles className="w-5 h-5" /><span className="text-xs font-mono uppercase tracking-wider">Comment progresser ?</span></div>
            <p className="text-sm text-zinc-300">{reco.message}</p>
            <div className="mt-4 space-y-2">
              {reco.actions.map((a, i) => (
                <div key={i} className="flex items-center gap-3 bg-[#0F0F13] border border-zinc-800 rounded-2xl p-3" data-testid={`reco-action-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{a.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{a.category}</p>
                  </div>
                  <span className="font-mono text-xs font-semibold shrink-0" style={{ color: CATEGORY_META[a.category_key]?.color }}>+{a.points} {a.category}</span>
                </div>
              ))}
            </div>
            <button onClick={() => navigate("/assistant", { state: { prefill: reco.assistant_prompt } })} data-testid="create-plan-btn"
              className="mt-4 inline-flex items-center gap-2 px-5 h-11 rounded-xl gold-gradient text-black font-semibold hover:opacity-90">
              <Sparkles className="w-5 h-5" /> Créer mon plan
            </button>
          </div>
        </div>
      )}

      {/* Score evolution chart */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="score-evolution">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">Évolution de mon Score de progression</h2>
          <div className="flex gap-1 bg-zinc-900 rounded-full p-1">
            {RANGES.map((r) => (
              <button key={r.k} onClick={() => setRange(r.k)} data-testid={`range-${r.k}`}
                className={`px-3 h-7 rounded-full text-xs font-medium transition-colors ${range === r.k ? "gold-gradient text-black" : "text-zinc-400 hover:text-zinc-100"}`}>{r.l}</button>
            ))}
          </div>
        </div>
        {chartData.length < 2 ? (
          <p className="text-sm text-zinc-500 py-12 text-center">Pas encore assez de données. Continue à agir pour voir ton score évoluer !</p>
        ) : (
          <div className="h-56" data-testid="score-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A35" />
                <XAxis dataKey="day" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} domain={[0, 1000]} />
                <Tooltip contentStyle={{ background: "#0F0F13", border: "1px solid #2A2A35", borderRadius: 12, color: "#FAFAFA" }} />
                <Line type="monotone" dataKey="score" stroke="#F59E0B" strokeWidth={2.5} dot={{ fill: "#F59E0B", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Stats + XP history */}
      <div className="grid grid-cols-3 gap-4">
        <MiniStat icon={Zap} label="XP total" value={data.total_xp} />
        <MiniStat icon={Trophy} label="Projets finis" value={data.stats.projects_done} />
        <MiniStat icon={CheckCircle2} label="Tâches finies" value={data.stats.tasks_done} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">Historique XP</h2>
          {data.transactions.length > 5 && (
            <button onClick={() => setShowAllXp((v) => !v)} data-testid="toggle-xp-history" className="text-sm text-amber-400 hover:underline">
              {showAllXp ? "Réduire" : "Voir tout l'historique"}
            </button>
          )}
        </div>
        {data.transactions.length === 0 ? (
          <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-8 text-center text-zinc-500 text-sm" data-testid="xp-history-empty">
            Termine des tâches pour commencer à gagner de l'XP.
          </div>
        ) : (
          <div className="bg-[#18181E] border border-zinc-800 rounded-3xl divide-y divide-zinc-800/70 overflow-hidden" data-testid="xp-history">
            {txns.map((tx) => (
              <div key={tx.id} className="flex items-center gap-4 p-4">
                <div className="w-9 h-9 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0"><TrendingUp className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{tx.reason}</p>
                  <p className="text-xs text-zinc-500">{fmtDate(tx.created_at)}</p>
                </div>
                <span className="font-mono text-sm font-semibold text-amber-400 shrink-0">+{tx.amount} XP</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Level roadmap */}
      <div>
        <h2 className="font-display text-xl font-bold mb-4">Feuille de route des niveaux</h2>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="levels-roadmap">
          {data.levels.map((l) => {
            const reached = data.total_xp >= l.xp;
            const current = l.level === lvl.level;
            return (
              <div key={l.level} data-testid={`level-${l.level}`}
                className={`flex items-center gap-3 p-4 rounded-2xl border ${current ? "border-amber-500/50 bg-amber-500/10" : reached ? "border-zinc-800 bg-[#18181E]" : "border-zinc-800/60 bg-[#131318]"}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-display font-bold ${reached ? "gold-gradient text-black" : "bg-zinc-800 text-zinc-500"}`}>
                  {reached ? l.level : <Lock className="w-4 h-4" />}
                </div>
                <div className="flex-1"><p className={`font-medium ${reached ? "text-zinc-100" : "text-zinc-500"}`}>{l.title}</p><p className="text-xs text-zinc-500 font-mono">{l.xp} XP</p></div>
                {current && <span className="text-xs font-mono text-amber-400">Actuel</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Explanation modal */}
      <AnimatePresence>
        {showExplain && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="explain-modal">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowExplain(false)} />
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
              className="relative w-full sm:max-w-md bg-[#18181E] border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-bold">Pourquoi mon score est-il de {data.artist_score} ?</h3>
                <button onClick={() => setShowExplain(false)} className="text-zinc-500 hover:text-zinc-200"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-2">
                {data.explanation.rows.map((r) => (
                  <div key={r.key} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">{r.label} : <span className="text-zinc-100 font-medium">{r.score}</span> <span className="text-zinc-600">({r.weight_pct}%)</span></span>
                    <span className="font-mono text-amber-400">→ {r.weighted} pts</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-sm pt-3 mt-2 border-t border-zinc-800">
                  <span className="font-semibold">Total</span>
                  <span className="font-mono font-bold gold-text text-lg">{data.explanation.total} / 1000</span>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-4">L'XP mesure ton activité. Le Score de progression mesure la maturité globale de ton parcours, pondérée par catégorie.</p>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ScoreRing({ value }) {
  const pct = Math.min(100, value / 10);
  const r = 52, c = 2 * Math.PI * r;
  return (
    <div className="relative w-32 h-32">
      <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#2A2A35" strokeWidth="10" />
        <circle cx="60" cy="60" r={r} fill="none" stroke="#F59E0B" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-3xl font-extrabold gold-text leading-none" data-testid="artist-score-value">{value}</span>
        <span className="text-xs text-zinc-500">/ 1000</span>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 text-center card-hover">
      <Icon className="w-5 h-5 text-amber-400 mx-auto mb-2" />
      <p className="font-display text-2xl font-extrabold">{value}</p>
      <p className="text-[11px] text-zinc-500 mt-0.5">{label}</p>
    </div>
  );
}
